import { useSelector } from 'react-redux'
import { useCallback } from 'react'
import { authFetch } from '../../services/authFetch'
import { URLS } from '../../const/nav'

export const useAdminAPI = () => {
  const token = useSelector(state => state.user?.token)

  const getHeaders = useCallback(() => ({
    'Content-Type': 'application/json',
    Authorization: token || ''
  }), [token])

  // GET /entities
  const fetchEntities = useCallback(async (endpoint) => {
    try {
      const response = await authFetch(`${URLS.BACKEND_URL}/${endpoint}`, {
        method: 'GET',
        headers: getHeaders()
      })

      if (!response.ok) {
        throw new Error(`Error fetching ${endpoint}: ${response.statusText}`)
      }

      return await response.json()
    } catch (error) {
      console.error(error)
      throw error
    }
  }, [getHeaders])

  // POST /entities
  const createEntity = useCallback(async (endpoint, data) => {
    try {
      const response = await authFetch(`${URLS.BACKEND_URL}/${endpoint}`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(data)
      })

      if (!response.ok) {
        let backendMessage = ''

        try {
          const errorPayload = await response.json()
          backendMessage = errorPayload?.mensaje || errorPayload?.message || ''
        } catch (_) {}

        const errorMessage = backendMessage
          ? `Error creating entity in ${endpoint}: ${backendMessage}`
          : `Error creating entity in ${endpoint}: ${response.statusText}`
        const createError = new Error(errorMessage)
        createError.status = response.status
        createError.backendMessage = backendMessage
        throw createError
      }

      return await response.json()
    } catch (error) {
      console.error(error)
      throw error
    }
  }, [getHeaders])

  // PUT /entities/:id
  const updateEntity = useCallback(async (endpoint, id, data) => {
    try {
      const response = await authFetch(`${URLS.BACKEND_URL}/${endpoint}/${id}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(data)
      })

      if (!response.ok) {
        let backendMessage = ''

        try {
          const errorPayload = await response.json()
          backendMessage = errorPayload?.mensaje || errorPayload?.message || ''
        } catch (_) {}

        const errorMessage = backendMessage
          ? `Error updating entity in ${endpoint}: ${backendMessage}`
          : `Error updating entity in ${endpoint}: ${response.statusText}`
        const updateError = new Error(errorMessage)
        updateError.status = response.status
        updateError.backendMessage = backendMessage
        throw updateError
      }

      return await response.json()
    } catch (error) {
      console.error(error)
      throw error
    }
  }, [getHeaders])

  // DELETE /entities/:id
  const deleteEntity = useCallback(async (endpoint, id) => {
    try {
      const response = await authFetch(`${URLS.BACKEND_URL}/${endpoint}/${id}`, {
        method: 'DELETE',
        headers: getHeaders()
      })

      if (!response.ok) {
        let backendMessage = ''

        try {
          const errorPayload = await response.json()
          backendMessage = errorPayload?.mensaje || errorPayload?.message || ''
        } catch (_) {}

        const errorMessage = backendMessage
          ? `Error deleting entity in ${endpoint}: ${backendMessage}`
          : `Error deleting entity in ${endpoint}: ${response.statusText}`
        const deleteError = new Error(errorMessage)
        deleteError.status = response.status
        deleteError.backendMessage = backendMessage
        throw deleteError
      }

      return await response.json()
    } catch (error) {
      console.error(error)
      throw error
    }
  }, [getHeaders])

  // POST /usuarios/:id/blanquear-contrasenia
  const resetPassword = useCallback(async (id) => {
    try {
      const response = await authFetch(`${URLS.BACKEND_URL}/usuarios/${id}/blanquear-contrasenia`, {
        method: 'POST',
        headers: getHeaders()
      })

      if (!response.ok) {
        let backendMessage = ''

        try {
          const errorPayload = await response.json()
          backendMessage = errorPayload?.mensaje || errorPayload?.message || ''
        } catch (_) {}

        const errorMessage = backendMessage
          ? `Error resetting password for user ${id}: ${backendMessage}`
          : `Error resetting password for user ${id}: ${response.statusText}`
        const resetError = new Error(errorMessage)
        resetError.status = response.status
        resetError.backendMessage = backendMessage
        throw resetError
      }

      return await response.json()
    } catch (error) {
      console.error(error)
      throw error
    }
  }, [getHeaders])

  // GET /usuarios/check-username
  const checkUsernameAvailability = useCallback(async (username) => {
    try {
      const response = await authFetch(`${URLS.BACKEND_URL}/usuarios/check-username?username=${encodeURIComponent(username)}`, {
        method: 'GET',
        headers: getHeaders()
      })

      if (!response.ok) {
        let backendMessage = ''
        try {
          const errorPayload = await response.json()
          backendMessage = errorPayload?.mensaje || errorPayload?.message || ''
        } catch (_) {}

        const error = new Error(backendMessage || response.statusText)
        error.status = response.status
        error.backendMessage = backendMessage
        throw error
      }

      return await response.json()
    } catch (error) {
      console.error(error)
      throw error
    }
  }, [getHeaders])

  return {
    fetchEntities,
    createEntity,
    updateEntity,
    deleteEntity,
    resetPassword,
    checkUsernameAvailability
  }
}
